<template>
  <button
    v-tooltip="user && user.first_name ? user.first_name + ' ' + user.last_name : 'User Info Not Available'"
    class="user-circle"
    :style="sizeStyle"
  >
    <template v-if="user && user.username">
      <img
        v-if="!showFallback"
        :src="imageUrl"
        :alt="user.username"
        @error="showFallback = true"
      >
      <div
        v-if="showFallback"
        :style="fontStyle"
        class="initials-fallback"
      >
        {{ formatInitials() }}
      </div>
    </template>
    <template v-else>
      <!-- Generic placeholder for public datasets -->
      <svg :style="fontStyle" viewBox="0 0 24 24" fill="currentColor" class="generic-avatar">
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
      </svg>
    </template>
  </button>
</template>

<script setup>
import {
  toRefs, computed, ref, watch,
} from 'vue';

const props = defineProps({
  user: {
    type: Object,
    default: () => {},
  },
  size: {
    type: [Number, String],
    default: null,
  },
});

const {
  user,
  size,
} = toRefs(props);

const showFallback = ref(false);

watch(() => user.value?.username, () => {
  showFallback.value = false;
});

const imageUrl = computed(() => `/avatar/${user.value.username}`);

const sizeStyle = computed(() => {
  let w = size.value;
  let h = size.value;
  if (typeof size.value === 'number') {
    w = `${w}px`;
  }
  if (typeof size.value === 'number') {
    h = `${h}px`;
  }

  return {
    width: w,
    'max-width': w,
    height: h,
    'max-height': h,
  };
});

const fontStyle = computed(() => {
  const size2 = sizeStyle.value.width;
  return {
    fontSize: `calc(${size2} / 2)`,
    lineHeight: sizeStyle.value.height,
    width: sizeStyle.value.width,
    height: sizeStyle.value.height,
    textAlign: 'center',
  };
});

function formatInitials() {
  if (user.value && user.value.first_name && user.value.last_name) {
    return (user.value.first_name.charAt(0) + user.value.last_name.charAt(0)).toUpperCase();
  }
  if (user.value && user.value.username) {
    return user.value.username.substring(0, 2).toUpperCase();
  }
  return '?';
}

</script>

<style lang="scss" scoped>
.user-circle {
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: 50%;
  color: inherit;
  text-transform: uppercase;
  background: transparent;
  cursor: pointer;
  padding: 0;

  img {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }

  .initials-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.1);
  }

  .generic-avatar {
    width: 100%;
    height: 100%;
    opacity: 0.5;
  }
}
</style>
